import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  getRedirectResult,
} from 'firebase/auth';
import { auth, googleProvider, appleProvider } from './firebase';
import { createUserDocument } from './services/users/users.service';
import { signInWithApple, signInWithEmail, signUpWithEmail } from './services/authService';
import { isNativeApp } from 'utils/platformHelpers';
import { clearWidgetSession } from 'services/widget/widgetBridge';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = (props) => {
  const children = props?.children;
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const signInWithGoogle = async () => {
    if (isNativeApp()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const nativeResult = await FirebaseAuthentication.signInWithGoogle();
      const idToken = nativeResult.credential?.idToken;
      if (!idToken) throw new Error('Google Sign-In did not return an ID token.');
      const { signInWithCredential, GoogleAuthProvider } = await import('firebase/auth');
      const credential = GoogleAuthProvider.credential(idToken);
      const result = await signInWithCredential(auth, credential);
      return result.user;
    }
    if (!googleProvider) {
      throw new Error(
        'Google Sign-In is not available. Please sign in with email.'
      );
    }
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      throw error;
    }
  };

  const signInWithAppleProvider = async () => {
    if (isNativeApp()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      // skipNativeAuth: the plugin must NOT sign in to the native Firebase SDK.
      // Apple's ID token + nonce are single-use; if the native layer consumes
      // them, our JS signInWithCredential below fails with
      // auth/missing-or-invalid-nonce ("Duplicate credential received").
      // We rely on the JS SDK for auth state, so sign in there exactly once.
      const nativeResult = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true });
      const idToken = nativeResult.credential?.idToken;
      if (!idToken) throw new Error('Apple Sign-In did not return an ID token.');
      const { OAuthProvider, signInWithCredential, updateProfile } = await import('firebase/auth');
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken,
        rawNonce: nativeResult.credential?.nonce,
      });
      const result = await signInWithCredential(auth, credential);
      // Apple returns the name only on the first authorization; persist it if
      // Firebase doesn't already have a display name for this user.
      const nativeName = nativeResult.user?.displayName;
      if (nativeName && !result.user.displayName) {
        await updateProfile(result.user, { displayName: nativeName });
      }
      return result.user;
    }
    return signInWithApple();
  };

  // Apple requires apps that support account deletion to revoke the user's Apple
  // token when they delete their account (App Store Guideline 5.1.1(v)). This must
  // run before the account is deleted, and Apple requires a fresh sign-in first.
  const revokeAppleAccessIfNeeded = async () => {
    const currentUser = auth.currentUser;
    const isAppleUser = currentUser?.providerData?.some(
      (info) => info.providerId === 'apple.com'
    );
    if (!isAppleUser) return;

    if (isNativeApp()) {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      // Re-auth natively (without skipNativeAuth) so the native SDK has a current
      // user for revokeToken, and to obtain a fresh Apple authorization code.
      const reauth = await FirebaseAuthentication.signInWithApple();
      const authorizationCode = reauth.credential?.authorizationCode;
      if (!authorizationCode) {
        throw new Error('Apple did not return an authorization code to revoke.');
      }
      await FirebaseAuthentication.revokeAccessToken({ token: authorizationCode });
      return;
    }

    const { signInWithPopup, OAuthProvider, revokeAccessToken } = await import('firebase/auth');
    const result = await signInWithPopup(auth, appleProvider);
    const credential = OAuthProvider.credentialFromResult(result);
    const accessToken = credential?.accessToken;
    if (!accessToken) {
      throw new Error('Apple did not return an access token to revoke.');
    }
    await revokeAccessToken(auth, accessToken);
  };

  const logout = async () => {
    try {
      if (isNativeApp()) {
        await clearWidgetSession();
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.signOut();
      }
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    // Skip redirect result on native: it triggers cross-origin requests to Google
    // that are blocked by CORS (origin capacitor://localhost). Redirect-based auth
    // is not used in the Capacitor app.
    if (!isNativeApp()) {
      getRedirectResult(auth)
        .then((result) => {
          if (result && result.user) {
            createUserDocument(result.user)
              .then((createdUser) => {
                setUserProfile(createdUser);
              })
              .catch(() => {});
          }
        })
        .catch((error) => {
          const errorMessage = error.message || '';
          const isMissingStateError =
            error.code === 'auth/missing-or-invalid-nonce' ||
            error.code === 'auth/argument-error' ||
            errorMessage.includes('missing initial state') ||
            errorMessage.includes('sessionStorage');
          if (!isMissingStateError) {
            console.error('Error processing redirect result:', error);
          }
        });
    }

    let loadingFinished = false;
    const finishLoading = () => {
      if (!loadingFinished) {
        loadingFinished = true;
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const createdUser = await createUserDocument(firebaseUser);
          setUserProfile(createdUser);
        } catch (error) {
          // Error creating user document
        }
      } else {
        setUserProfile(null);
        if (isNativeApp()) {
          clearWidgetSession();
        }
      }
    });

    // Wait until persisted auth state is restored before routing (avoids false sign-out flash).
    auth.authStateReady().then(finishLoading).catch(finishLoading);

    // Safety: if authStateReady never resolves (e.g. CORS blocks Firebase on native),
    // stop loading so the app shows sign-in instead of spinning forever.
    const timeoutId = setTimeout(finishLoading, 10000);

    return () => {
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, []);

  const value = {
    user,
    userProfile,
    setUserProfile,
    loading,
    signInWithGoogle,
    signInWithApple: signInWithAppleProvider,
    signInWithEmail,
    signUpWithEmail,
    revokeAppleAccessIfNeeded,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
